/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量打标模式 Context
 *
 * 状态层 — 管理"进入/退出批量打标模式"和"应用标签的回调"。
 *
 * 设计思路:
 *   - 进入批量模式后,所有 Photo 卡片切到"勾选模式":隐藏 + 按钮、整卡片可点击勾选
 *   - 勾选状态复用 redux 的 selectionConfig.checkedPhotos / checkAll
 *     (跟"顶部批量添加标签"共享同一份勾选状态,避免双轨)
 *   - 应用标签的具体 filter/auth/buildParams 由各 PhotosList 自己实现 (photov2 / ai-photo-king / photo 三套),
 *     Provider 接收 applyTagsHandler 注入,本 Context 只做"模式开关 + 透传 handler"
 *   - 退出模式时清空 selected (由调用方 dispatch,见 BatchTagBar 的取消按钮)
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

export interface ApplyTagsResult {
  boundPhotoCount: number
  boundLabelCount: number
}

export type ApplyTagsHandler = (params: {
  labelIDs: string[]
  labelNames: string[]
}) => Promise<ApplyTagsResult>

export interface UnbindTagsResult {
  removedRelCount: number
  affectedPhotoCount: number
  affectedLabelCount: number
}

/** 批量移除标签 handler,跟 applyTagsHandler 镜像。由各 PhotosList 注入具体实现。 */
export type UnbindTagsHandler = (params: {
  labelIDs: string[]
  labelNames: string[]
}) => Promise<UnbindTagsResult>

interface BatchTagModeContextValue {
  /** 是否处于批量模式 */
  isBatchMode: boolean
  /** 进入批量模式,可选传一个初始要勾选的 photo seq */
  enter: (initialPhotoSeq?: number) => void
  /** 退出批量模式 */
  exit: () => void
  /** 应用标签,由 Provider 注入具体实现 (各 PhotosList 各一套 filter 构造逻辑) */
  applyTags: ApplyTagsHandler
  /** 移除标签,跟 applyTags 镜像 */
  unbindTags: UnbindTagsHandler
  /** Provider 注入的"勾选某 photo seq"回调 (一般转发 dispatch pushCheckedPhoto),
   *  让"批量"按钮在 enter 时把当前 photo 加入 selected */
  pushChecked?: (photoSeq: number) => void
  /** 当前承载页, 用于埋点 fromPage 字段. photov2 / ai_photo_king / photo 三选一 */
  fromPage: 'photov2' | 'ai_photo_king' | 'photo'
  /** 哨兵: 是否被 BatchTagModeProvider 包裹.
   *  默认 false (没被包裹) — 共享 Photo 组件在「分类文件夹 / 加水印 / 选照片器」
   *  等非打标场景下也复用,默认 ctx 必须告诉它"这里没启用打标 UI",
   *  避免 + 按钮 / chip overlay 渗到不相关页面.
   *  Provider 内部 value 显式置 true. */
  isProviderMounted: boolean
}

const noopApply: ApplyTagsHandler = async () => ({
  boundPhotoCount: 0,
  boundLabelCount: 0
})
const noopUnbind: UnbindTagsHandler = async () => ({
  removedRelCount: 0,
  affectedPhotoCount: 0,
  affectedLabelCount: 0
})

const Ctx = createContext<BatchTagModeContextValue>({
  isBatchMode: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  enter: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  exit: () => {},
  applyTags: noopApply,
  unbindTags: noopUnbind,
  fromPage: 'photov2',
  isProviderMounted: false
})

export interface BatchTagModeProviderProps {
  /** 应用标签到当前 selected 照片;一般在 PhotosList 内组装好 filter 后调用 batchBindLabels */
  applyTagsHandler: ApplyTagsHandler
  /** 移除标签 handler;一般在 PhotosList 内复用同一 filter 构造逻辑调用 batchUnbindLabels.
   *  允许不传 — 此时底部"批量移除"按钮不渲染 */
  unbindTagsHandler?: UnbindTagsHandler
  /** 把某 photo seq 加入 selected,供"批量"按钮在 enter 时把触发卡片自动勾选 */
  pushChecked?: (photoSeq: number) => void
  /** 退出批量模式时调用 (一般清空 redux 的 checkedPhotos) */
  onExit?: () => void
  /** 当前承载页, 用于埋点 fromPage 字段. 各 PhotosList 自己传, 默认 'photov2' */
  fromPage?: 'photov2' | 'ai_photo_king' | 'photo'
  children: React.ReactNode
}

export const BatchTagModeProvider: React.FC<BatchTagModeProviderProps> = ({
  applyTagsHandler,
  unbindTagsHandler,
  pushChecked,
  onExit,
  fromPage = 'photov2',
  children
}) => {
  const [isBatchMode, setIsBatchMode] = useState(false)

  const enter = useCallback(
    (initialPhotoSeq?: number) => {
      setIsBatchMode(true)
      if (
        initialPhotoSeq !== null &&
        initialPhotoSeq !== undefined &&
        pushChecked
      ) {
        pushChecked(initialPhotoSeq)
      }
    },
    [pushChecked]
  )

  const exit = useCallback(() => {
    setIsBatchMode(false)
    onExit && onExit()
  }, [onExit])

  const value = useMemo<BatchTagModeContextValue>(
    () => ({
      isBatchMode,
      enter,
      exit,
      applyTags: applyTagsHandler,
      unbindTags: unbindTagsHandler || noopUnbind,
      pushChecked,
      fromPage,
      isProviderMounted: true
    }),
    [
      isBatchMode,
      enter,
      exit,
      applyTagsHandler,
      unbindTagsHandler,
      pushChecked,
      fromPage
    ]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useBatchTagMode(): BatchTagModeContextValue {
  return useContext(Ctx)
}
